import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Lock, Store, Mic, Square, Receipt, Gift, Flame, X, Settings, Trash2, HelpCircle, BookOpen } from "lucide-react";
import { getGuide } from "@/lib/userGuide";
import { startChat, sendMessage, sendVoice, createCheckout, payWithRazorpay, getReferralLink, clearChat, deleteMessage } from "@/lib/api";
import SetupGuide from "@/components/SetupGuide";
import Bubble from "@/components/ChatBubble";
import InvoiceHistory from "@/components/InvoiceHistory";
import ShopSettings from "@/components/ShopSettings";
import { useLang } from "@/lib/i18n";

const BOTS = {
  kirana: {
    name: "KashAssist", tag: "Bill + GST + Khata", emoji: <Store size={18} />,
    suggestions: ["Ramesh ko 2kg atta ₹45/kg aur 1 Maggi ₹14 ka bill banao", "Sabun pe kitna GST lagta hai?"],
    plan: "kirana_pro", price: "₹99/mo",
  },
};

const inviteFriend = async () => {
  const link = getReferralLink();
  const text = `Yeh AI assistant try karo — pehle 3 uses free, mere link se aane par dono ko +1 extra free use! 👉 ${link}`;
  if (navigator.share) {
    try {
      await navigator.share({ title: "KashAssist", text });
    } catch (e) {
      console.debug("share dismissed", e);
    }
  } else {
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  }
};

function ChatHeader({ B, bot, typing, recording, streak, subscribed, subDaysLeft, freeLeft, onHistory, onReferral, onSettings, onClearChat, t }) {
  return (
    <div className="bg-[#006D5B] text-white px-4 py-3 flex items-center gap-3 z-10">
      <div className="w-9 h-9 rounded-full bg-[#FFC107] text-[#111827] flex items-center justify-center">
        {B.emoji}
      </div>
      <div className="flex-1">
        <p className="font-semibold text-sm leading-tight">{B.name}</p>
        <p className="text-[11px] text-white/70">{typing ? t("typing") : recording ? t("recording") : t("online")}</p>
      </div>
      {bot === "tutor" && streak > 0 && (
        <span data-testid="streak-badge" title={`${streak} din ki padhai streak!`}
          className="flex items-center gap-0.5 bg-white/20 text-white text-[10px] font-bold px-2 py-1 rounded-full">
          <Flame size={11} className="text-[#FFC107]" /> {streak}
        </span>
      )}
      {bot === "kirana" && (
        <>
          <button
            data-testid="invoice-history-btn"
            onClick={onHistory}
            title="Bill history"
            className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors duration-150"
          >
            <Receipt size={17} />
          </button>
          <button
            data-testid="shop-settings-btn"
            onClick={onSettings}
            title="Dukaan settings — naam, bill number, WhatsApp link"
            className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors duration-150"
          >
            <Settings size={17} />
          </button>
          <button
            data-testid="clear-chat-btn"
            onClick={onClearChat}
            title="Chat saaf karo (bills aur khata safe rahenge)"
            className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors duration-150"
          >
            <Trash2 size={16} />
          </button>
        </>
      )}
      <button
        data-testid="invite-friend-btn"
        onClick={onReferral}
        title="Dost ko invite karo — dono ko +1 free use"
        className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors duration-150"
      >
        <Gift size={17} />
      </button>
      {!subscribed ? (
        <span data-testid="free-uses-counter" className="bg-[#FFC107] text-[#111827] text-[10px] font-bold px-2 py-1 rounded-full">
          {t("freeLeft", { n: freeLeft })}
        </span>
      ) : (
        <span data-testid="pro-badge"
          className={`text-[10px] font-bold px-2 py-1 rounded-full ${
            subDaysLeft != null && subDaysLeft < 5 ? "bg-[#FFC107] text-[#111827]" : "bg-white/20 text-white"}`}>
          PRO ✨{subDaysLeft != null ? ` · ${subDaysLeft} din` : ""}
        </span>
      )}
    </div>
  );
}

function PaywallOverlay({ B, bot, paying, onUpgrade, onUpgradeCard, t }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="paywall-overlay"
      className="absolute inset-x-0 bottom-0 top-14 backdrop-blur-md bg-white/70 flex flex-col items-center justify-center p-6 text-center z-20"
    >
      <div className="w-14 h-14 rounded-full bg-[#006D5B] text-white flex items-center justify-center mb-4">
        <Lock size={24} />
      </div>
      <h3 className="font-heading font-bold text-xl mb-2">{t("paywallTitle")}</h3>
      <p className="text-sm text-gray-600 mb-5">{t("paywallSub", { plan: `${B.name} Pro`, price: B.price })}</p>
      <button
        data-testid="paywall-checkout-btn"
        onClick={onUpgrade}
        disabled={paying}
        className="bg-[#006D5B] text-white font-semibold px-8 py-3 rounded-full shadow-lg hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-60"
      >
        {paying ? "Processing..." : t("upgradeUpi", { price: B.price })}
      </button>
      <button
        data-testid="paywall-card-btn"
        onClick={onUpgradeCard}
        disabled={paying}
        className="mt-2.5 text-[12px] text-[#006D5B] font-semibold underline underline-offset-2 disabled:opacity-60"
      >
        Ya card se pay karo (Stripe)
      </button>
      <button
        data-testid="paywall-invite-btn"
        onClick={inviteFriend}
        className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-[#8a6d00] bg-[#FFC107]/25 px-3.5 py-1.5 rounded-full hover:bg-[#FFC107]/40 transition-colors duration-150"
      >
        <Gift size={13} /> {t("inviteFree")}
      </button>
      <p className="text-[11px] text-gray-400 mt-3">Card test mode · 4242 4242 4242 4242</p>
    </motion.div>
  );
}

function ReferralModal({ referral, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="referral-modal"
      className="absolute inset-0 z-40 bg-black/40 flex items-center justify-center p-5"
    >
      <div className="bg-white rounded-3xl p-6 w-full max-w-[300px] text-center relative">
        <button
          data-testid="referral-close-btn"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
        >
          <X size={15} />
        </button>
        <div className="w-12 h-12 mx-auto rounded-full bg-[#FFC107]/25 text-[#8a6d00] flex items-center justify-center mb-3">
          <Gift size={22} />
        </div>
        <h3 className="font-heading font-bold text-lg mb-1">Dost bulao, free uses pao</h3>
        <p className="text-[12px] text-gray-500 mb-4">Har dost jo join kare — dono ko +1 free use (max 5)</p>
        <div className="flex justify-center gap-6 mb-4">
          <div>
            <p data-testid="referral-count" className="font-heading font-bold text-2xl text-[#006D5B]">{referral.count}</p>
            <p className="text-[10px] text-gray-500">dost joined</p>
          </div>
          <div>
            <p data-testid="referral-bonus" className="font-heading font-bold text-2xl text-[#006D5B]">+{referral.bonus}</p>
            <p className="text-[10px] text-gray-500">free uses earned</p>
          </div>
        </div>
        <div className="flex gap-1 justify-center mb-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={`dot-${i}`} className={`w-6 h-1.5 rounded-full ${i < referral.count ? "bg-[#FFC107]" : "bg-gray-200"}`} />
          ))}
        </div>
        <button
          data-testid="referral-share-btn"
          onClick={inviteFriend}
          className="w-full bg-[#006D5B] text-white font-semibold text-sm py-3 rounded-full shadow-lg hover:-translate-y-0.5 transition-transform duration-200"
        >
          Link Share Karo →
        </button>
      </div>
    </motion.div>
  );
}

export default function ChatSimulator({ fixedBot, onHowTo }) {
  const [bot, setBot] = useState(fixedBot || "kirana");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [freeLeft, setFreeLeft] = useState(3);
  const [subscribed, setSubscribed] = useState(false);
  const [subDaysLeft, setSubDaysLeft] = useState(null);
  const [paywalled, setPaywalled] = useState(false);
  const [paying, setPaying] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [streak, setStreak] = useState(0);
  const [referral, setReferral] = useState({ count: 0, bonus: 0 });
  const [showReferral, setShowReferral] = useState(false);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const { t, lang } = useLang();

  const loadSession = useCallback(async (b) => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      const { data } = await startChat(b, ref);
      setMessages(data.messages);
      setFreeLeft(data.free_uses_left);
      setSubscribed(data.subscribed);
      setSubDaysLeft(data.sub_days_left ?? null);
      setStreak(data.streak || 0);
      setReferral({ count: data.referral_count || 0, bonus: data.bonus || 0 });
      setPaywalled(!data.subscribed && data.free_uses_left <= 0);
    } catch (e) {
      console.error("start chat failed", e);
    }
  }, []);

  useEffect(() => { loadSession(bot); }, [bot, loadSession]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing, paywalled]);

  const applyReply = (data) => {
    if (data.reply?.streak) setStreak(data.reply.streak);
    if (!data.subscribed) {
      setFreeLeft(Math.max(0, data.free_uses_left));
      if (data.paywalled) setPaywalled(true);
    } else {
      setSubscribed(true);
    }
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || typing) return;
    setInput("");
    const requestId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setMessages((m) => [...m, { role: "user", kind: "text", text: msg, localKey: requestId, created_at: new Date().toISOString() }]);
    setTyping(true);
    const attach = (m, data) => [
      ...m.map((x) => (x.localKey === requestId ? { ...x, id: data.reply.user_message_id } : x)),
      { role: "bot", ...data.reply, created_at: new Date().toISOString() },
    ];
    try {
      const { data } = await sendMessage(bot, msg, requestId);
      setMessages((m) => attach(m, data));
      applyReply(data);
    } catch (e) {
      console.error("send failed, retrying once", e);
      try {
        await new Promise((r) => setTimeout(r, 1500));
        const { data } = await sendMessage(bot, msg, requestId);
        setMessages((m) => attach(m, data));
        applyReply(data);
      } catch (e2) {
        console.error("send retry failed", e2);
        setMessages((m) => [...m, { role: "bot", kind: "text", text: "Oops, kuch गड़बड़ ho gayi. Dubara try karo! 🙏", created_at: new Date().toISOString() }]);
      }
    }
    setTyping(false);
  };

  const deleteMsg = async (m) => {
    setMessages((prev) => prev.filter((x) => x !== m));
    const id = m.id || m.message_id;
    if (id) {
      try { await deleteMessage(id); } catch (e) { console.error("delete msg failed", e); }
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Poori chat delete karni hai? Bills, khata aur stock safe rahenge.")) return;
    try {
      await clearChat(bot);
      await loadSession(bot);
    } catch (e) { console.error("clear chat failed", e); }
  };

  const sendVoiceNote = async (blob) => {
    setTyping(true);
    setMessages((m) => [...m, { role: "user", kind: "text", text: "🎤 Voice note bheji ja rahi hai...", temp: true, created_at: new Date().toISOString() }]);
    try {
      const { data } = await sendVoice(bot, blob);
      setMessages((m) => [
        ...m.filter((x) => !x.temp),
        { role: "user", kind: "text", text: `🎤 ${data.transcript}`, id: data.reply?.user_message_id, created_at: new Date().toISOString() },
        { role: "bot", ...data.reply, created_at: new Date().toISOString() },
      ]);
      applyReply(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Voice samajh nahi aayi, dubara try karo! 🙏";
      setMessages((m) => [...m.filter((x) => !x.temp), { role: "bot", kind: "text", text: detail, created_at: new Date().toISOString() }]);
    }
    setTyping(false);
  };

  const toggleRecord = async () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        if (blob.size > 0) sendVoiceNote(blob);
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      console.error("mic access denied", e);
    }
  };

  const upgrade = async () => {
    setPaying(true);
    try {
      await payWithRazorpay(BOTS[bot].plan);
    } catch (e) {
      if (e?.message === "dismissed") { setPaying(false); return; }
      try {
        const { data } = await createCheckout(BOTS[bot].plan);
        window.location.href = data.checkout_url;
      } catch (e2) {
        console.error("checkout failed", e2);
        setPaying(false);
      }
    }
  };

  const upgradeCard = async () => {
    setPaying(true);
    try {
      const { data } = await createCheckout(BOTS[bot].plan);
      window.location.href = data.checkout_url;
    } catch (e) {
      console.error("checkout failed", e);
      setPaying(false);
    }
  };

  const B = BOTS[bot];
  const locked = paywalled && !subscribed;

  return (
    <div className="w-full max-w-[380px]">
      {/* Bot picker */}
      {!fixedBot && (
        <div data-testid="bot-picker" className="flex bg-white rounded-full p-1 shadow-md border border-black/5 mb-4">
          {Object.entries(BOTS).map(([key, b]) => (
            <button
              key={key}
              data-testid={`bot-toggle-${key}`}
              onClick={() => setBot(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                bot === key ? "bg-[#006D5B] text-white" : "text-gray-500 hover:text-[#006D5B]"
              }`}
            >
              {b.emoji} {b.name.split(" ")[0]}
            </button>
          ))}
        </div>
      )}

      {onHowTo && (
        <button data-testid="how-to-use-btn" onClick={onHowTo}
          className="w-full mb-3 flex items-center justify-center gap-2 bg-[#FFC107] text-[#111827] font-bold text-sm py-3 rounded-full shadow-md hover:-translate-y-0.5 transition-transform duration-150">
          <BookOpen size={16} /> {getGuide(lang).ui.chatBtn}
        </button>
      )}

      {/* Phone frame */}
      <div data-testid="chat-simulator" className="rounded-[2rem] overflow-hidden shadow-[0_20px_50px_rgba(0,109,91,0.25)] border border-black/10 bg-white flex flex-col h-[600px] relative">
        <ChatHeader
          B={B} bot={bot} typing={typing} recording={recording} streak={streak}
          subscribed={subscribed} subDaysLeft={subDaysLeft} freeLeft={freeLeft}
          onHistory={() => setShowHistory(true)} onReferral={() => setShowReferral(true)}
          onSettings={() => setShowSettings(true)} onClearChat={clearAll} t={t}
        />

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll wa-doodle p-3 flex flex-col gap-2.5">
          {(messages.length <= 1 || showGuide) && (
            <div className="max-w-[92%]">
              <button
                data-testid="chat-setup-guide-btn"
                onClick={() => setShowGuide((v) => !v)}
                className="flex items-center gap-1.5 bg-white border border-[#006D5B]/25 text-[#006D5B] text-[11px] font-bold px-3 py-1.5 rounded-full shadow-sm hover:bg-[#006D5B]/5 transition-colors duration-150 mb-2"
              >
                <HelpCircle size={13} /> {showGuide ? "Guide band karo" : "Naye ho? 2-min setup guide dekho"}
              </button>
              {showGuide && <SetupGuide />}
            </div>
          )}
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <Bubble key={m.id || m.message_id || m.localKey || `${m.role}-${m.created_at}-${i}`} msg={m} botType={bot} onDelete={deleteMsg} />
            ))}
          </AnimatePresence>
          {typing && (
            <div className="flex justify-start">
              <div data-testid="typing-indicator" className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm flex gap-1">
                <span className="typing-dot w-1.5 h-1.5 bg-gray-400 rounded-full inline-block" />
                <span className="typing-dot w-1.5 h-1.5 bg-gray-400 rounded-full inline-block" />
                <span className="typing-dot w-1.5 h-1.5 bg-gray-400 rounded-full inline-block" />
              </div>
            </div>
          )}
        </div>

        {/* Suggestions */}
        {messages.length <= 1 && !paywalled && (
          <div className="px-3 pb-1 pt-2 bg-[#E5DDD5]/0 absolute bottom-16 left-0 right-0 flex flex-col gap-1.5">
            {B.suggestions.map((s, i) => (
              <button
                key={s}
                data-testid={`suggestion-chip-${i}`}
                onClick={() => send(s)}
                className="bg-white/95 text-left text-[12px] text-[#006D5B] font-medium px-3 py-2 rounded-xl border border-[#006D5B]/20 shadow-sm hover:bg-[#006D5B] hover:text-white transition-colors duration-200"
              >
                💬 {s}
              </button>
            ))}
          </div>
        )}

        {/* Paywall overlay */}
        <AnimatePresence>
          {locked && (
            <PaywallOverlay B={B} bot={bot} paying={paying} onUpgrade={upgrade} onUpgradeCard={upgradeCard} t={t} />
          )}
        </AnimatePresence>

        {/* Input */}
        <div className="bg-[#F0F0F0] px-3 py-2.5 flex items-center gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={recording ? t("recPlaceholder") : t("msgPlaceholder")}
            disabled={locked || recording}
            className="flex-1 bg-white rounded-full px-4 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-[#006D5B]/40 disabled:opacity-50"
          />
          <button
            data-testid="chat-mic-btn"
            onClick={toggleRecord}
            disabled={typing || locked}
            title={recording ? "Stop recording" : "Voice note"}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-150 disabled:opacity-50 ${
              recording ? "bg-red-500 text-white animate-pulse" : "bg-white text-[#006D5B] border border-[#006D5B]/30"
            }`}
          >
            {recording ? <Square size={15} /> : <Mic size={17} />}
          </button>
          <button
            data-testid="chat-send-btn"
            onClick={() => send()}
            disabled={typing || recording || locked}
            className="w-10 h-10 rounded-full bg-[#006D5B] text-white flex items-center justify-center hover:scale-105 transition-transform duration-150 disabled:opacity-50"
          >
            <Send size={17} />
          </button>
        </div>

        {/* Referral progress modal */}
        <AnimatePresence>
          {showReferral && <ReferralModal referral={referral} onClose={() => setShowReferral(false)} />}
        </AnimatePresence>

        {/* Invoice history overlay */}
        <AnimatePresence>
          {showHistory && <InvoiceHistory onClose={() => setShowHistory(false)} />}
        </AnimatePresence>

        {/* Shop settings overlay */}
        <AnimatePresence>
          {showSettings && (
            <ShopSettings
              onClose={() => setShowSettings(false)}
              onLinked={(data) => {
                if (data?.subscribed?.[bot]) {
                  setSubscribed(true);
                  setPaywalled(false);
                }
              }}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
